import { useState, useEffect, useRef } from 'react'
import { Volume2, Play, Pause, Square, X, GripVertical, ChevronLeft } from 'lucide-react'
import { cn } from '@/utils/cn'
import { screenReader } from '@/utils/screenReader'
import { useTranslation } from 'react-i18next'

const MOBILE_BREAKPOINT = 768

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(false)
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < MOBILE_BREAKPOINT)
    check()
    window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [])
  return isMobile
}

interface ScreenReaderButtonProps {
  className?: string
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'middle-right'
}

export default function ScreenReaderButton({ 
  className,
  position = 'top-right'
}: ScreenReaderButtonProps) {
  const { t } = useTranslation()
  const isMobile = useIsMobile()
  const [screenReaderEnabled, setScreenReaderEnabled] = useState(false)
  const [isReading, setIsReading] = useState(false)
  const [showControls, setShowControls] = useState(false)
  const [positionState, setPositionState] = useState<{ x: number; y: number } | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 })
  const [hasMoved, setHasMoved] = useState(false)
  const [isPanelOpen, setIsPanelOpen] = useState(false)
  const [panelDragOffset, setPanelDragOffset] = useState(0)
  const panelTouchStartX = useRef<number | null>(null)
  const buttonRef = useRef<HTMLDivElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const intervalRef = useRef<NodeJS.Timeout | null>(null)

  // Load saved position from localStorage
  useEffect(() => {
    const savedPosition = localStorage.getItem('screenReaderButtonPosition')
    if (savedPosition) {
      try {
        const pos = JSON.parse(savedPosition)
        setPositionState(pos)
      } catch (e) {
        console.error('Failed to parse saved position:', e)
      }
    }
  }, [])

  // Save position to localStorage when it changes
  useEffect(() => {
    if (positionState) {
      localStorage.setItem('screenReaderButtonPosition', JSON.stringify(positionState))
    }
  }, [positionState])

  // Mobile panel: swipe/drag to close
  const handlePanelTouchStart = (e: React.TouchEvent) => {
    panelTouchStartX.current = e.touches[0].clientX
    setPanelDragOffset(0)
  }
  const handlePanelTouchMove = (e: React.TouchEvent) => {
    if (panelTouchStartX.current === null) return
    const x = e.touches[0].clientX
    const delta = x - panelTouchStartX.current
    // Swipe right (positive delta) to close panel
    setPanelDragOffset(Math.max(0, delta))
  }
  const handlePanelTouchEnd = () => {
    const threshold = 60
    if (panelDragOffset > threshold) {
      setIsPanelOpen(false)
      setPanelDragOffset(0)
    } else {
      setPanelDragOffset(0)
    }
    panelTouchStartX.current = null
  }

  // Handle mouse down for dragging
  const handleDragStart = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (!buttonRef.current) return
    
    const rect = buttonRef.current.getBoundingClientRect()
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    })
    setIsDragging(true)
  }

  // Handle mouse move for dragging
  useEffect(() => {
    if (!isDragging) return

    const handleMouseMove = (e: MouseEvent) => {
      const newX = e.clientX - dragOffset.x
      const newY = e.clientY - dragOffset.y
      
      // Check if mouse has moved significantly (more than 5px)
      if (!hasMoved) {
        const moved = Math.abs(e.movementX) > 5 || Math.abs(e.movementY) > 5
        if (moved) {
          setHasMoved(true)
        }
      }
      
      // Keep button within viewport bounds
      const maxX = window.innerWidth - (buttonRef.current?.offsetWidth || 0)
      const maxY = window.innerHeight - (buttonRef.current?.offsetHeight || 0)
      
      setPositionState({
        x: Math.max(0, Math.min(newX, maxX)),
        y: Math.max(0, Math.min(newY, maxY))
      })
    }

    const handleMouseUp = () => {
      setIsDragging(false)
      // Reset hasMoved after a short delay
      setTimeout(() => {
        setHasMoved(false)
      }, 100)
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [isDragging, dragOffset, hasMoved])

  // Screen Reader effects
  useEffect(() => {
    screenReader.setEnabled(screenReaderEnabled)
    
    if (screenReaderEnabled) {
      if (screenReader.isSupported()) {
        // Read page title first, then main content
        setTimeout(() => {
          if (screenReader.getEnabled()) {
            screenReader.readPageTitle()
            // Read main content after title
            setTimeout(() => {
              if (screenReader.getEnabled()) {
                screenReader.readMainContent()
              }
            }, 2000)
          }
        }, 500)
      }

      // Monitor reading state
      intervalRef.current = setInterval(() => {
        setIsReading(screenReader.getIsReading())
      }, 100)

      // Add hover listeners to read elements when hovered
      let lastHoveredElement: HTMLElement | null = null
      const isUserControllingRef = { current: false }

      const handleMouseOver = (e: MouseEvent) => {
        const target = e.target as HTMLElement
        
        // Skip if hovering on screen reader button itself or its children
        if (target.closest('.screen-reader-button') || target.closest('.accessibility-toolbar')) {
          return
        }

        // Skip if user is controlling playback manually
        if (isUserControllingRef.current) {
          return
        }

        // Skip if same element (to prevent re-reading)
        if (target === lastHoveredElement) {
          return
        }

        // Skip audio/video elements and their containers
        if (target.closest('audio, video, [class*="radio"], [class*="audio"], [class*="player"]')) {
          return
        }

        // Skip elements marked to be ignored by screen reader
        if (target.closest('[data-screen-reader-ignore="true"]')) {
          return
        }

        lastHoveredElement = target

        // Always read on hover - this is the main feature
        // Stop current reading and read the hovered element
        screenReader.readElement(target, false)
      }

      const handleMouseOut = (e: MouseEvent) => {
        const relatedTarget = e.relatedTarget as HTMLElement
        // Cancel any pending hover reads when leaving
        if (!relatedTarget || !lastHoveredElement?.contains(relatedTarget)) {
          screenReader.cancelHoverRead()
          lastHoveredElement = null
        }
      }

      // Add event listeners with capture phase to catch all events
      document.addEventListener('mouseover', handleMouseOver, true)
      document.addEventListener('mouseout', handleMouseOut, true)

      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current)
        }
        document.removeEventListener('mouseover', handleMouseOver, true)
        document.removeEventListener('mouseout', handleMouseOut, true)
        screenReader.cancelHoverRead()
        lastHoveredElement = null
      }
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
      screenReader.stop()
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
  }, [screenReaderEnabled])

  const positionClasses = {
    'top-right': 'top-4 right-4',
    'top-left': 'top-4 left-4',
    'bottom-right': 'bottom-4 right-4',
    'bottom-left': 'bottom-4 left-4',
    'middle-right': 'top-1/2 -translate-y-1/2 right-4',
  }

  if (!screenReader.isSupported()) {
    return null
  }

  // ——— موبايل: زر عائم + لوحة جانبية تُسحب للإغلاق ———
  if (isMobile) {
    return (
      <>
        {/* موبايل: زر واضح (أيقونة + نص) حتى يكون الـ UX معبّر — فوق زر ChatGPT لتجنب التداخل */}
        <button
          type="button"
          onClick={() => { setIsPanelOpen(true); setShowControls(true) }}
          className="fixed bottom-20 right-4 sm:bottom-24 sm:right-6 z-30 flex items-center justify-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 md:px-4 py-2 sm:py-2.5 rounded-full bg-white dark:bg-gray-800 text-primary-600 dark:text-primary-400 border-2 border-primary-500 shadow-lg hover:bg-gray-50 dark:hover:bg-gray-700 focus:ring-2 focus:ring-primary-400 focus:ring-offset-2 dark:focus:ring-offset-gray-900 transition-transform active:scale-95 screen-reader-button min-w-[44px] sm:min-w-auto"
          aria-label={t('accessibility.screenReader')}
          title={t('accessibility.screenReader')}
        >
          <Volume2 className="w-4 h-4 sm:w-5 sm:h-5 shrink-0 flex-shrink-0" />
          <span className="hidden min-[400px]:inline text-xs sm:text-sm font-semibold whitespace-nowrap">{t('accessibility.screenReader')}</span>
        </button>

        {isPanelOpen && (
          <div
            className="fixed inset-0 bg-black/30 z-40 transition-opacity"
            aria-hidden
            onClick={() => { setIsPanelOpen(false); setPanelDragOffset(0) }}
          />
        )}

        <div
          ref={panelRef}
          role="dialog"
          aria-label={t('accessibility.title')}
          className={cn(
            'fixed top-0 bottom-0 right-0 z-50 w-[85vw] max-w-[320px] bg-white dark:bg-gray-800 shadow-2xl flex flex-col transition-transform duration-200 ease-out screen-reader-button',
            !isPanelOpen && 'pointer-events-none'
          )}
          style={{
            transform: isPanelOpen ? `translateX(${panelDragOffset}px)` : 'translateX(100%)',
          }}
          onTouchStart={handlePanelTouchStart}
          onTouchMove={handlePanelTouchMove}
          onTouchEnd={handlePanelTouchEnd}
        >
          <div className="flex items-center justify-between shrink-0 py-3 px-4 border-b border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={() => { setIsPanelOpen(false); setPanelDragOffset(0) }}
              className="flex items-center gap-1 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 touch-manipulation"
              aria-label={t('accessibility.close')}
            >
              <ChevronLeft className="w-5 h-5" />
              <span className="text-sm font-medium">{t('accessibility.close')}</span>
            </button>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              {t('accessibility.title')}
            </h3>
            <button
              type="button"
              onClick={() => { setIsPanelOpen(false); setPanelDragOffset(0) }}
              className="p-1.5 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 rounded-lg"
              aria-label={t('accessibility.close')}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            <button
              type="button"
              onClick={() => {
                const newState = !screenReaderEnabled
                setScreenReaderEnabled(newState)
                if (newState && screenReader.isSupported()) {
                  setTimeout(() => {
                    screenReader.readPageTitle()
                    setTimeout(() => screenReader.readMainContent(), 2000)
                  }, 300)
                }
              }}
              className={cn(
                'w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-semibold transition-colors',
                screenReaderEnabled ? 'bg-primary-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-2 border-primary-500'
              )}
              aria-label={t('accessibility.screenReader')}
            >
              <Volume2 className={cn('w-5 h-5', isReading && 'animate-pulse')} />
              <span>
                {screenReaderEnabled ? t('accessibility.screenReaderActive') : t('accessibility.screenReader')}
              </span>
            </button>

            {screenReaderEnabled && (
              <div className="space-y-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-2">
                  {isReading ? (
                    <button
                      type="button"
                      onClick={() => { screenReader.pause(); setIsReading(false) }}
                      className="flex-1 flex items-center justify-center gap-2 px-3 py-2.5 bg-yellow-500 hover:bg-yellow-600 text-white rounded-lg"
                      aria-label={t('accessibility.pause')}
                    >
                      <Pause className="w-4 h-4" />
                      <span className="text-sm">{t('accessibility.pause')}</span>
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => {
                        const synth = (screenReader as any).synthInstance
                        if (screenReader.getIsReading() && synth?.paused) screenReader.resume()
                        else screenReader.readMainContent()
                        setIsReading(true)
                      }}
                      className="flex-1 flex items-center justify-center gap-2 px-3 py-2.5 bg-green-500 hover:bg-green-600 text-white rounded-lg"
                      aria-label={t('accessibility.play')}
                    >
                      <Play className="w-4 h-4" />
                      <span className="text-sm">{t('accessibility.play')}</span>
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => { screenReader.stop(); setIsReading(false) }}
                    className="flex items-center justify-center p-2.5 bg-red-500 hover:bg-red-600 text-white rounded-lg"
                    aria-label={t('accessibility.stop')}
                  >
                    <Square className="w-4 h-4" />
                  </button>
                </div>
                <button
                  type="button"
                  onClick={() => { screenReader.readMainContent(); setIsReading(true) }}
                  className="w-full px-3 py-2.5 bg-primary-500 hover:bg-primary-600 text-white rounded-lg text-sm"
                >
                  {t('accessibility.readMain')}
                </button>
                <p className="text-xs text-gray-500 dark:text-gray-400 pt-2">
                  {t('accessibility.hoverTip')}
                </p>
              </div>
            )}
          </div>

          <div className="shrink-0 py-2 text-center">
            <span className="text-xs text-gray-400 dark:text-gray-500">
              {t('accessibility.dragToClose', 'اسحب لليمين للإغلاق')}
            </span>
          </div>
        </div>
      </>
    )
  }

  // Use saved position or default position
  const style = positionState
    ? {
        left: `${positionState.x}px`,
        top: `${positionState.y}px`,
        right: 'auto',
        bottom: 'auto',
      }
    : undefined

  return (
    <div
      ref={buttonRef}
      className={cn(
        'fixed z-50 screen-reader-button',
        positionState ? '' : positionClasses[position],
        isDragging && 'cursor-move',
        className
      )}
      style={style}
    >
      {/* Main Toggle Button */}
      <div className="flex items-center gap-1">
        {/* Drag Handle */}
        <div
          onMouseDown={handleDragStart}
          className={cn(
            'p-1 rounded-l-full cursor-move hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors flex items-center',
            screenReaderEnabled
              ? 'bg-primary-700 dark:bg-primary-600 text-white'
              : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
          )}
          title={t('accessibility.dragToMove')}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
            }
          }}
        >
          <GripVertical className="w-4 h-4" />
        </div>

        {/* Main Toggle Button */}
        <button
          onClick={(e) => {
            // Don't toggle if we just finished dragging or if mouse moved
            if (isDragging || hasMoved) {
              e.preventDefault()
              return
            }
            const newState = !screenReaderEnabled
            setScreenReaderEnabled(newState)
            setShowControls(newState)
            if (newState && screenReader.isSupported()) {
              // Read page title first, then main content
              setTimeout(() => {
                screenReader.readPageTitle()
                setTimeout(() => {
                  screenReader.readMainContent()
                }, 2000)
              }, 300)
            }
          }}
          className={cn(
            'flex items-center gap-2 px-4 py-3 rounded-r-full shadow-lg transition-all',
            screenReaderEnabled
              ? 'bg-primary-600 dark:bg-primary-500 text-white hover:bg-primary-700 dark:hover:bg-primary-600'
              : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 border-2 border-primary-500 dark:border-primary-400 border-l-0'
          )}
          aria-label={t('accessibility.screenReader')}
          title={t('accessibility.screenReader')}
        >
          <Volume2 className={cn('w-5 h-5', isReading && 'animate-pulse')} />
          <span className="text-sm font-semibold">
            {screenReaderEnabled ? t('accessibility.screenReaderActive') : t('accessibility.screenReader')}
          </span>
        </button>
      </div>

      {/* Control Panel */}
      {showControls && screenReaderEnabled && (
        <div className="absolute top-full mt-2 right-0 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 p-4 min-w-[250px]">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              {t('accessibility.title')}
            </h3>
            <button
              onClick={() => setShowControls(false)}
              className="p-1 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 rounded"
              aria-label={t('accessibility.close')}
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-2">
              {isReading ? (
                <button
                  onClick={() => {
                    screenReader.pause()
                    setIsReading(false)
                  }}
                  className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-yellow-500 hover:bg-yellow-600 text-white rounded-lg transition-colors"
                  aria-label={t('accessibility.pause')}
                >
                  <Pause className="w-4 h-4" />
                  <span className="text-sm">{t('accessibility.pause')}</span>
                </button>
              ) : (
                <button
                  onClick={() => {
                    const synth = (screenReader as any).synthInstance
                    if (screenReader.getIsReading() && synth?.paused) {
                      screenReader.resume()
                    } else {
                      screenReader.readMainContent()
                    }
                    setIsReading(true)
                  }}
                  className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors"
                  aria-label={t('accessibility.play')}
                >
                  <Play className="w-4 h-4" />
                  <span className="text-sm">{t('accessibility.play')}</span>
                </button>
              )}
              <button
                onClick={() => {
                  screenReader.stop()
                  setIsReading(false)
                }}
                className="flex items-center justify-center gap-2 px-3 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors"
                aria-label={t('accessibility.stop')}
              >
                <Square className="w-4 h-4" />
              </button>
            </div>

            <button
              onClick={() => {
                screenReader.readMainContent()
                setIsReading(true)
              }}
              className="w-full px-3 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors text-sm"
            >
              {t('accessibility.readMain')}
            </button>

            <div className="text-xs text-gray-500 dark:text-gray-400 pt-2 border-t border-gray-200 dark:border-gray-700">
              {t('accessibility.hoverTip')}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

